import { recoveryPoolInnerCall } from "./sdk";
import { ethers } from "ethers";
import { SHIELDED_POOL_JOINSPLIT_V2_INTERFACE } from "./sdk";
import { computeNPK } from "../joinsplit/keys";
import type { JoinSplitNote } from "../joinsplit/types";
import { BN254_SCALAR_FIELD, createFlatJoinSplitV2Note } from "./notes";
import {
  decodePrivateSwap,
  privateSwapDeployment,
  PRIVATE_SWAP_INTERFACE,
  privateSwapRouteHash,
} from "./privateSwapConfig";

/** Secret recovery material, encrypted with the settlement journal before broadcast. */
export interface FlatJoinSplitV2SwapOutputSeed {
  recipientMpk: bigint;
  random: bigint;
  outputNpk: string;
  outputTokenAddress: string;
  outputPoolAddress?: string;
}

export const SWAP_OUTPUT_EVENTS = new ethers.Interface([
  "event PrivateSwap(bytes32[] nullifiers,address inputToken,address outputToken,uint256 inputAmount,uint256 outputAmount,bytes32 outputCommitment)",
  "event LeafInsertedInTree(uint256 indexed treeIndex,bytes32 leaf,uint256 leafIndex)",
]);

export async function validateSwapOutputSeed(
  seed: FlatJoinSplitV2SwapOutputSeed,
  calldata: string,
) {
  for (const value of [seed.recipientMpk, seed.random]) {
    if (
      typeof value !== "bigint" ||
      value <= 0n ||
      value >= BN254_SCALAR_FIELD
    ) {
      throw new Error("Swap recovery secret is not a canonical nonzero scalar");
    }
  }
  calldata = recoveryPoolInnerCall(calldata);
  const current = decodePrivateSwap(calldata);
  if (current) {
    const { route, leg, recovery } = current;
    if (
      seed.outputPoolAddress?.toLowerCase() !==
        privateSwapDeployment()?.pool.toLowerCase() ||
      seed.outputNpk.toLowerCase() !== route.outputNpk.toLowerCase() ||
      (await computeNPK(seed.recipientMpk, seed.random)) !==
        BigInt(seed.outputNpk) ||
      ethers.getAddress(seed.outputTokenAddress) !== route.outputToken
    )
      throw Error("Swap recovery seed does not match its route");
    return {
      spend: {
        nullifiers: leg.settlement.nullifiers,
        commitments: leg.settlement.commitments,
        changeCommitment: undefined,
      },
      swap: {
        outputToken: route.outputToken,
        minOutputAmount: route.minimumOut,
      },
      current,
    };
  }
  const decoded = SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.parseTransaction({
    data: calldata,
  });
  if (
    !decoded ||
    decoded.name !== "privateSwap" ||
    SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.encodeFunctionData(
      decoded.fragment,
      decoded.args,
    ).toLowerCase() !== calldata.toLowerCase()
  ) {
    throw new Error(
      "Swap recovery requires the exact canonical privateSwap intent",
    );
  }
  const [spend, swap] = decoded.args;
  if (
    !ethers.isHexString(seed.outputNpk, 32) ||
    seed.outputNpk.toLowerCase() !== String(swap.outputNpk).toLowerCase() ||
    (await computeNPK(seed.recipientMpk, seed.random)) !==
      BigInt(seed.outputNpk) ||
    ethers.getAddress(seed.outputTokenAddress) !==
      ethers.getAddress(swap.outputToken)
  ) {
    throw new Error(
      "Swap recovery seed does not match the proof-bound output key/token",
    );
  }
  return { spend, swap, current: undefined };
}

/** The caller must first authenticate transaction, successful receipt and finality. */
export async function recoverFlatJoinSplitV2SwapOutput(params: {
  provider: ethers.Provider;
  receipt: ethers.TransactionReceipt;
  pool: string;
  chainId: number | bigint;
  calldata: string;
  seed: FlatJoinSplitV2SwapOutputSeed;
}): Promise<JoinSplitNote> {
  const { spend, swap, current } = await validateSwapOutputSeed(
    params.seed,
    params.calldata,
  );
  if (params.receipt.status !== 1)
    throw new Error("Swap settlement did not succeed");
  if (current) return recoverPrivateSwapOutput(params, current);
  const pool = ethers.getAddress(params.pool);
  const events: ethers.LogDescription[] = [];
  for (const log of params.receipt.logs) {
    if (ethers.getAddress(log.address) !== pool) continue;
    const topic = log.topics[0]?.toLowerCase();
    if (
      !SWAP_OUTPUT_EVENTS.fragments.some(
        (fragment) =>
          fragment.type === "event" &&
          (fragment as ethers.EventFragment).topicHash.toLowerCase() === topic,
      )
    )
      continue;
    if ((log as ethers.Log).removed)
      throw new Error("Swap receipt contains a removed log");
    const parsed = SWAP_OUTPUT_EVENTS.parseLog(log);
    if (parsed) events.push(parsed);
  }
  const swaps = events.filter((event) => event.name === "PrivateSwap");
  if (swaps.length !== 1)
    throw new Error("Swap receipt must contain exactly one PrivateSwap event");
  const event = swaps[0].args;
  const expectedNullifiers = Array.from(spend.nullifiers, String).map((value) =>
    value.toLowerCase(),
  );
  const actualNullifiers = Array.from(event.nullifiers, String).map((value) =>
    value.toLowerCase(),
  );
  if (
    expectedNullifiers.join(":") !== actualNullifiers.join(":") ||
    ethers.getAddress(event.inputToken) !== ethers.getAddress(spend.token) ||
    ethers.getAddress(event.outputToken) !==
      ethers.getAddress(swap.outputToken) ||
    BigInt(event.inputAmount) !== BigInt(spend.amount) - BigInt(spend.fee) ||
    BigInt(event.outputAmount) < BigInt(swap.minOutputAmount)
  ) {
    throw new Error("Swap event does not match the recorded settlement intent");
  }
  const commitment = String(event.outputCommitment).toLowerCase();
  const insertions = events.filter(
    (candidate) =>
      candidate.name === "LeafInsertedInTree" &&
      String(candidate.args.leaf).toLowerCase() === commitment,
  );
  if (insertions.length !== 1)
    throw new Error("Swap output is missing its unique tree insertion");
  const treeIndex = Number(insertions[0].args.treeIndex);
  const leafIndex = Number(insertions[0].args.leafIndex);
  if (
    !Number.isSafeInteger(treeIndex) ||
    treeIndex < 0 ||
    !Number.isSafeInteger(leafIndex) ||
    leafIndex < 0 ||
    leafIndex >= 1 << 24
  )
    throw new Error("Swap output location is invalid");
  // Never use the pre-proof current tree: an exact-spend swap can settle after rollover.
  const data = SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.encodeFunctionData(
    "computeTokenHashForTree",
    [treeIndex, params.seed.outputTokenAddress, 0, 0],
  );
  const raw = await params.provider.call({
    to: pool,
    data,
    blockTag: params.receipt.blockNumber,
  } as any);
  const [tokenHash] = SHIELDED_POOL_JOINSPLIT_V2_INTERFACE.decodeFunctionResult(
    "computeTokenHashForTree",
    raw,
  );
  const note = await createFlatJoinSplitV2Note({
    recipientMpk: params.seed.recipientMpk,
    random: params.seed.random,
    chainId: params.chainId,
    poolAddress: pool,
    treeIndex,
    tokenAddress: params.seed.outputTokenAddress,
    tokenHash,
    value: BigInt(event.outputAmount),
  });
  if (note.commitment.toLowerCase() !== commitment) {
    throw new Error("Swap output commitment does not match the recovered note");
  }
  return { ...note, leafIndex };
}

async function recoverPrivateSwapOutput(
  params: Parameters<typeof recoverFlatJoinSplitV2SwapOutput>[0],
  current: NonNullable<ReturnType<typeof decodePrivateSwap>>,
): Promise<JoinSplitNote> {
  const deployment = privateSwapDeployment()!,
    { route, recovery } = current,
    hash = privateSwapRouteHash(route);
  if (
    route.sourcePool.toLowerCase() !== params.pool.toLowerCase() ||
    Number(params.chainId) !== 84532
  )
    throw Error("Swap source does not match");
  const events = params.receipt.logs.flatMap((log) => {
    if ((log as ethers.Log).removed)
      throw Error("Swap receipt contains a removed log");
    if (
      ![deployment.coordinator, deployment.pool].includes(
        ethers.getAddress(log.address),
      )
    )
      return [];
    try {
      const parsed = PRIVATE_SWAP_INTERFACE.parseLog(log);
      return parsed
        ? [{ address: ethers.getAddress(log.address), ...parsed }]
        : [];
    } catch {
      return [];
    }
  });
  const completion = events.filter(
    (e) =>
      e.address === deployment.coordinator &&
      e.name === (recovery ? "PrivateSwapRecovered" : "PrivateSwapCompleted") &&
      e.args.routeHash === hash,
  );
  const outputs = events.filter(
    (e) =>
      e.address === deployment.pool &&
      e.name === "PrivateSwapOutput" &&
      e.args.routeHash === hash,
  );
  if (completion.length !== 1 || outputs.length !== 1)
    throw Error("Swap output confirmation is missing");
  const output = outputs[0].args,
    c = completion[0].args,
    token = recovery ? route.inputToken : route.outputToken;
  if (
    output.npk !== route.outputNpk ||
    output.token !== token ||
    output.commitment !== c.outputCommitment ||
    (recovery
      ? output.amount !== route.amountIn
      : output.amount < route.minimumOut) ||
    (!recovery &&
      (c.sourcePool !== route.sourcePool ||
        c.outputPool !== deployment.pool ||
        c.inputToken !== route.inputToken ||
        c.outputToken !== token ||
        c.amountIn !== route.amountIn ||
        c.amountOut !== output.amount))
  )
    throw Error("Swap receipt does not match the accepted route");
  const leaves = events.filter(
    (e) =>
      e.address === deployment.pool &&
      e.name === "LeafInsertedInTree" &&
      e.args.leaf === output.commitment &&
      e.args.treeIndex === output.treeIndex,
  );
  if (leaves.length !== 1)
    throw Error("Swap output is missing its unique insertion");
  const treeIndex = Number(output.treeIndex),
    leafIndex = Number(leaves[0].args.leafIndex);
  if (
    !Number.isSafeInteger(treeIndex) ||
    treeIndex < 0 ||
    !Number.isSafeInteger(leafIndex) ||
    leafIndex < 0 ||
    leafIndex >= 1 << 24
  )
    throw Error("Invalid swap output location");
  const pool = new ethers.Contract(
    deployment.pool,
    SHIELDED_POOL_JOINSPLIT_V2_INTERFACE,
    params.provider,
  );
  const tokenHash = await pool.computeTokenHashForTree(treeIndex, token, 0, 0, {
    blockTag: params.receipt.blockNumber,
  });
  if (tokenHash !== output.tokenHash) throw Error("Swap token domain mismatch");
  const note = await createFlatJoinSplitV2Note({
    recipientMpk: params.seed.recipientMpk,
    random: params.seed.random,
    chainId: params.chainId,
    poolAddress: deployment.pool,
    treeIndex,
    tokenAddress: token,
    tokenHash,
    value: output.amount,
  });
  if (note.commitment.toLowerCase() !== output.commitment.toLowerCase())
    throw Error("Swap output commitment mismatch");
  return { ...note, leafIndex };
}
